import React, { useState, useEffect, useRef } from 'react';
import { groceryAPI } from '../services/api';

function Grocery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickAddName, setQuickAddName] = useState('');
  const [processingIds, setProcessingIds] = useState(new Set());
  const processingRef = useRef(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', price: '' });

  useEffect(() => {
    fetchItems();
    const interval = setInterval(() => { fetchItems(); }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchItems = async () => {
    try {
      const response = await groceryAPI.getAll();
      setItems(response.data);
    } catch (error) { console.error('Error fetching grocery items:', error); }
    finally { setLoading(false); }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddName.trim()) return;
    try {
      await groceryAPI.create({ name: quickAddName });
      setQuickAddName('');
      fetchItems();
    } catch (error) { console.error('Error adding item:', error); alert('Failed to add item'); }
  };

  const handleToggle = async (id) => {
    if (processingRef.current.has(id)) return;
    processingRef.current.add(id);
    setProcessingIds(new Set(processingRef.current));
    setItems(prev => prev.map(item => item.id === id ? { ...item, is_checked: !item.is_checked } : item));
    try {
      await groceryAPI.toggle(id);
      await fetchItems();
    } catch (error) { console.error('Error toggling item:', error); await fetchItems(); }
    finally {
      setTimeout(() => {
        processingRef.current.delete(id);
        setProcessingIds(new Set(processingRef.current));
      }, 500);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, price: item.price || '' });
  };

  const handleEditSave = async (id) => {
    if (!editForm.name.trim()) return;
    try {
      await groceryAPI.update(id, { name: editForm.name.trim(), price: editForm.price || null });
      setEditingId(null);
      fetchItems();
    } catch (error) { console.error('Error updating item:', error); }
  };

  const handleDelete = async (id) => {
    if (processingRef.current.has(id)) return;
    processingRef.current.add(id);
    setProcessingIds(new Set(processingRef.current));
    setItems(prev => prev.filter(item => item.id !== id));
    try {
      await groceryAPI.delete(id);
      await fetchItems();
    } catch (error) { console.error('Error deleting item:', error); await fetchItems(); }
    finally {
      setTimeout(() => {
        processingRef.current.delete(id);
        setProcessingIds(new Set(processingRef.current));
      }, 500);
    }
  };

  const handleClearChecked = async () => {
    if (window.confirm('Clear all checked items?')) {
      setItems(prev => prev.filter(item => !item.is_checked));
      try { await groceryAPI.clearChecked(); await fetchItems(); }
      catch (error) { console.error('Error clearing checked items:', error); await fetchItems(); }
    }
  };

  const uncheckedItems = items.filter(item => !item.is_checked);
  const checkedItems = items.filter(item => item.is_checked);
  const runningTotal = items.filter(i => i.price).reduce((sum, i) => sum + parseFloat(i.price), 0);
  const checkedTotal = checkedItems.filter(i => i.price).reduce((sum, i) => sum + parseFloat(i.price), 0);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Grocery List</h2>
          <p>Your shared family shopping list</p>
        </div>
        {checkedItems.length > 0 && (
          <button onClick={handleClearChecked} className="btn btn-secondary">
            Clear Checked ({checkedItems.length})
          </button>
        )}
      </div>

      <div className="card">
        {/* Quick Add */}
        <div className="form-section">
          <form onSubmit={handleQuickAdd} className="inline-form">
            <input type="text" className="form-input" placeholder="Add item..."
              value={quickAddName} onChange={(e) => setQuickAddName(e.target.value)} />
            <button type="submit" className="btn btn-primary">Add</button>
          </form>
        </div>

        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛒</div>
            <p className="empty-state-text">Your grocery list is empty</p>
          </div>
        ) : (
          <>
            {/* Unchecked Items */}
            {uncheckedItems.length > 0 && (
              <div>
                <div style={{
                  padding: '12px 24px 6px',
                  fontSize: '12px', fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  To Buy ({uncheckedItems.length})
                </div>
                {uncheckedItems.map((item) => (
                  <div key={item.id} className="list-item"
                    style={{ opacity: processingIds.has(item.id) ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                    <div className="checkbox-wrapper">
                      <input type="checkbox" className="checkbox" checked={item.is_checked}
                        onChange={() => handleToggle(item.id)} disabled={processingIds.has(item.id)} />
                    </div>
                    {editingId === item.id ? (
                      <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="text" className="form-input" value={editForm.name}
                          onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && handleEditSave(item.id)}
                          style={{ flex: 2, minWidth: '120px', padding: '6px 10px', fontSize: '13px' }}
                          autoFocus />
                        <input type="number" className="form-input" value={editForm.price}
                          onChange={e => setEditForm({ ...editForm, price: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && handleEditSave(item.id)}
                          placeholder="Price"
                          style={{ flex: 1, minWidth: '80px', padding: '6px 10px', fontSize: '13px' }}
                          step="0.01" min="0" />
                        <button onClick={() => handleEditSave(item.id)} className="btn btn-success btn-sm">Save</button>
                        <button onClick={() => setEditingId(null)} className="btn btn-secondary btn-sm">Cancel</button>
                      </div>
                    ) : (
                      <div className="list-item-content">
                        <div className="list-item-title">{item.name}</div>
                        <div className="list-item-subtitle">
                          {item.quantity && <span>{item.quantity}</span>}
                          {item.price && <span style={{ marginLeft: item.quantity ? '8px' : 0, color: 'var(--accent-coral)', fontWeight: 600 }}>${parseFloat(item.price).toFixed(2)}</span>}
                        </div>
                      </div>
                    )}
                    {editingId !== item.id && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleEdit(item)} className="btn btn-secondary btn-sm">Edit</button>
                        <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-sm btn-icon">×</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Running Total */}
            {runningTotal > 0 && (
              <div style={{
                padding: '12px 24px', borderTop: '1px solid var(--border-color)',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>
                  {checkedTotal > 0 ? `In Cart: $${checkedTotal.toFixed(2)}` : 'Running Total'}
                </span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent-coral)' }}>
                  ${runningTotal.toFixed(2)}
                </span>
              </div>
            )}

            {/* Checked Items */}
            {checkedItems.length > 0 && (
              <div>
                <div style={{
                  padding: '16px 24px 6px',
                  fontSize: '12px', fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  In Cart ({checkedItems.length})
                </div>
                {checkedItems.map((item) => (
                  <div key={item.id} className="list-item completed"
                    style={{ opacity: processingIds.has(item.id) ? 0.2 : 0.5, transition: 'opacity 0.2s' }}>
                    <div className="checkbox-wrapper">
                      <input type="checkbox" className="checkbox" checked={item.is_checked}
                        onChange={() => handleToggle(item.id)} disabled={processingIds.has(item.id)} />
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-title">{item.name}</div>
                      {item.quantity && <div className="list-item-subtitle">{item.quantity}</div>}
                    </div>
                    <button onClick={() => handleDelete(item.id)}
                      className="btn btn-danger btn-sm btn-icon">×</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Grocery;
